<template>
  <Teleport to="body">
    <div v-if="active" class="cmo-modal">

      <!-- Base map (current style, interactive) -->
      <div ref="baseMapEl" class="cmo-map-layer cmo-base"></div>

      <!-- Compare map (selected style, non-interactive, opacity-controlled) -->
      <div
        ref="overlayMapEl"
        class="cmo-map-layer cmo-overlay"
        :style="{ opacity: opacity / 100 }"
      ></div>

      <!-- Header -->
      <div class="cmo-header">
        <button class="cmo-close-btn" @click="close" aria-label="Close comparison">
          <span class="material-icons">close</span>
        </button>
        <span class="cmo-title">{{ styleName }}</span>
      </div>

      <!-- Bottom opacity bar -->
      <div class="cmo-footer">
        <div class="cmo-footer-label">Comparison opacity</div>
        <div class="cmo-slider-row">
          <input
            type="range"
            min="0"
            max="100"
            v-model.number="opacity"
            class="cmo-slider"
            aria-label="Comparison opacity"
          />
          <span class="cmo-pct">{{ opacity }}%</span>
        </div>
      </div>

    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue';
import mapboxgl from 'mapbox-gl';
import { MapDataCreditControl } from '../../../utils/helpers/MapDataCreditControl';

export default defineComponent({
  name: 'CompareMapsOverlay',
  props: {
    active:          { type: Boolean, default: false },
    styleUrl:        { type: String,  default: '' },
    styleName:       { type: String,  default: '' },
    baseStyleUrl:    { type: String,  default: '' },
    mapInstanceName: { type: String,  default: 'map' },
  },
  emits: ['exit'],
  data() {
    return {
      opacity:     70,
      baseMap:     null as mapboxgl.Map | null,
      overlayMap:  null as mapboxgl.Map | null,
      moveHandler: null as (() => void) | null,
      creditControl: null as MapDataCreditControl | null,
    };
  },

  watch: {
    active(val: boolean) {
      if (val) nextTick(() => this.initMaps());
      else     this.destroyMaps();
    },
  },

  methods: {
    initMaps() {
      if (this.baseMap) return; // already initialised — guard against double-open
      const mainMap = (window as Record<string, any>)[this.mapInstanceName] as mapboxgl.Map | null;
      const baseEl    = this.$refs.baseMapEl    as HTMLElement | undefined;
      const overlayEl = this.$refs.overlayMapEl as HTMLElement | undefined;
      if (!mainMap || !baseEl || !overlayEl) return;

      const center  = mainMap.getCenter();
      const zoom    = mainMap.getZoom();
      const bearing = mainMap.getBearing();
      const pitch   = mainMap.getPitch();

      // Base map — interactive, shows the current active style
      this.baseMap = new mapboxgl.Map({
        container:          baseEl,
        style:              this.baseStyleUrl || (mainMap.getStyle() as any),
        center, zoom, bearing, pitch,
        interactive:        true,
        attributionControl: false,
      });
      this.baseMap.addControl(new mapboxgl.NavigationControl(), 'top-right');
      // Either half can be showing Lidar, and neither has an AttributionControl. One control, on the
      // un-faded base map and clear of the header and the opacity bar, carries both styles' credits
      // — see setAdditionalSources.
      this.creditControl = new MapDataCreditControl();
      this.baseMap.addControl(this.creditControl, 'bottom-left');
      this.baseMap.once('load', () => this.baseMap?.resize());

      // Overlay map — non-interactive, synced to base map
      this.overlayMap = new mapboxgl.Map({
        container:          overlayEl,
        style:              this.styleUrl,
        center, zoom, bearing, pitch,
        interactive:        false,
        attributionControl: false,
      });
      this.overlayMap.on('styledata', () => {
        try {
          this.creditControl?.setAdditionalSources(this.overlayMap?.getStyle()?.sources as never);
        } catch {
          // Style still resolving; the next styledata event carries it.
        }
      });
      this.overlayMap.once('load', () => this.overlayMap?.resize());

      this.moveHandler = () => {
        if (!this.baseMap || !this.overlayMap) return;
        this.overlayMap.jumpTo({
          center:  this.baseMap.getCenter(),
          zoom:    this.baseMap.getZoom(),
          bearing: this.baseMap.getBearing(),
          pitch:   this.baseMap.getPitch(),
        });
      };
      this.baseMap.on('move', this.moveHandler);
    },

    destroyMaps() {
      if (this.moveHandler && this.baseMap) {
        this.baseMap.off('move', this.moveHandler);
        this.moveHandler = null;
      }
      this.baseMap?.remove();
      this.baseMap = null;
      this.overlayMap?.remove();
      this.overlayMap = null;
      // map.remove() calls onRemove on its controls; drop our handle so a reopen builds a fresh one
      // rather than feeding sources to a detached control.
      this.creditControl = null;
    },

    close() {
      this.destroyMaps();
      this.$emit('exit');
    },
  },

  beforeUnmount() {
    this.destroyMaps();
  },
});
</script>

<style scoped>
/* ── fullscreen blocking modal ── */
.cmo-modal {
  position: fixed;
  inset: 0;
  z-index: 1050;
  background: #17181d;
  overflow: hidden;
}

/* ── both map layers fill the modal ── */
.cmo-map-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.cmo-overlay {
  pointer-events: none; /* overlay is visual-only; base map handles interaction */
}

/* ── header bar ── */
.cmo-header {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(23, 24, 29, 0.88);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.cmo-close-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s ease;
}
.cmo-close-btn:hover { background: rgba(255, 255, 255, 0.22); }

.cmo-title {
  font-weight: 700;
  font-size: 1rem;
  color: #fff;
}

/* ── footer bar ── */
.cmo-footer {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding: 14px 20px 22px;
  background: rgba(23, 24, 29, 0.88);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.07);
}

.cmo-footer-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  color: #aaa;
  margin-bottom: 10px;
}

.cmo-slider-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.cmo-slider {
  flex: 1;
  -webkit-appearance: none;
  appearance: none;
  height: 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.15);
  outline: none;
  cursor: pointer;
}
.cmo-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fddf00;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
}
.cmo-slider::-moz-range-thumb {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #fddf00;
  cursor: pointer;
  border: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
}

.cmo-pct {
  font-weight: 700;
  font-size: 1rem;
  color: #fddf00;
  min-width: 46px;
  text-align: right;
}
</style>
